import { Box, Grid } from '@mui/material';
import { GridCard } from '../Card/Card';
import { Alarms } from '../Alarms/Alarms';
import { LineChart } from '../LineChart/LineChart';
import AdpMassFlowMeterLC from '../AdpMassFlowMeter/AdpMassFlowMeter';
import { useTranslation } from 'react-i18next';
import { LIVE_SESSION_NS } from './i18n';
import { NAVIGATION_NS } from '../../i18n/navigation';
import { CHART_COLORS, StreamDataPointKeys } from '../../types/api/profile';
import { IExtendedFiltersValues } from '../../stores/useFilters';
import { useMemo } from 'react';
import { CalendarDatePickerPreset } from '@diana/core';
import { useMessageStore } from '../../stores/useMessageStore';
import { HubsNames } from '../../stores/useSignalR';
import { TExtendedFilterValueRecord } from '../../utils/convertFiltersToQuery';

type GraphsSectionProps = {
  isEmerson: boolean;
  timeRange: TExtendedFilterValueRecord[];
  filtersValues: IExtendedFiltersValues | null;
};

export const GraphsSection = (props: GraphsSectionProps) => {
  const { t } = useTranslation([LIVE_SESSION_NS, NAVIGATION_NS]);
  const graphsData = useMessageStore((state) => state.messages?.[HubsNames.bunkeringDashboard]);
  const currentSessionData = useMessageStore((state) => state.messages?.[HubsNames.activeSessionBunkeringDashboard]);
  const isGraphsLoading = useMessageStore((state) => state.stableLoadingFlags?.[HubsNames.bunkeringDashboard]);
  const isCurrentSessionLoading = useMessageStore((state) => state.stableLoadingFlags?.[HubsNames.activeSessionBunkeringDashboard]);

  const preset = props.timeRange?.[0]?.preset;

  // Memoize the active data selection based on preset value
  const activeData = useMemo(() => {
    return preset === CalendarDatePickerPreset.CurrentSession ? currentSessionData : graphsData;
  }, [preset, currentSessionData, graphsData]);

  // Memoize the loading state based on preset value
  const isLoading = useMemo(() => {
    return preset === CalendarDatePickerPreset.CurrentSession
      ? isCurrentSessionLoading
      : isGraphsLoading;
  }, [preset, isCurrentSessionLoading, isGraphsLoading]);

  return (
    <Box>
      <Box component="section" sx={{ marginBottom: '8px' }}>
        <Grid container spacing={1}>
          <Grid item md={10} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:mfmReadings')} fullscreen={true}>
              <AdpMassFlowMeterLC
                values={activeData}
                loading={isLoading}
                isEmerson={props.isEmerson}
              />
            </GridCard>
          </Grid>
          <Grid item md={2} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:alarms')}>
              <Alarms filtersValues={props.filtersValues} />
            </GridCard>
          </Grid>
        </Grid>
      </Box>
      <Box component="section">
        <Grid container spacing={1}>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:massFlowRate')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.massFlowRate}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} MT/hour`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.massFlowRate]}
              />
            </GridCard>
          </Grid>
          {props.isEmerson && (
            <Grid item sm={6} xs={12}>
              <GridCard title={t('components/BunkeringDashboard:volumeFlowRate')} fullscreen={true}>
                <LineChart
                  key={StreamDataPointKeys.volumeFlowRate}
                  dataKey={StreamDataPointKeys.volumeFlowRate}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                      labels: {
                        formatter: function () {
                          return `${this.value} m³/hour`;
                        },
                      },
                    },
                  }}
                  data={activeData}
                  loading={isLoading}
                  color={CHART_COLORS[StreamDataPointKeys.volumeFlowRate]}
                />
              </GridCard>
            </Grid>
          )}
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:massTotal')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.massTotal}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} MT`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.massTotal]}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:volumeTotal')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.volumeTotal}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} m³`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.volumeTotal]}
              />
            </GridCard>
          </Grid>
          {props.isEmerson ? (
            <Grid item sm={6} xs={12}>
              <GridCard title={t('components/BunkeringDashboard:driveGain')} fullscreen={true}>
                <LineChart
                  dataKey={StreamDataPointKeys.driveGain}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                      labels: {
                        formatter: function () {
                          return `${this.value} %`;
                        },
                      },
                    },
                  }}
                  data={activeData}
                  loading={isLoading}
                  color={CHART_COLORS[StreamDataPointKeys.driveGain]}
                />
              </GridCard>
            </Grid>
          ) : (
            <Grid item sm={6} xs={12}>
              <GridCard title={t('components/BunkeringDashboard:tubeDamping')} fullscreen={true}>
                <LineChart
                  dataKey={StreamDataPointKeys.tubeDamping}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                    },
                  }}
                  data={activeData}
                  loading={isLoading}
                  color={CHART_COLORS[StreamDataPointKeys.tubeDamping]}
                />
              </GridCard>
            </Grid>
          )}
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:density')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.density}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} kg/m³`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.density]}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:temperature')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.temperature}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} °C`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.temperature]}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:pressure')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.pressure}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} bar`;
                      },
                    },
                  },
                }}
                data={activeData}
                loading={isLoading}
                color={CHART_COLORS[StreamDataPointKeys.pressure]}
              />
            </GridCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
